import { watch, onBeforeUnmount, ref, readonly } from 'vue';
import { defaultWindow } from '../utils/component-lib.esm2.js';
import { IN_BROWSER } from '../utils/component-lib.esm5.js';

/**
 * Reports changes to the dimensions of an Element's content or the border-box
 *
 * @param target
 * @param callback
 * @param options
 */
function useResizeObserver(target, callback) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    observeOnMount: true
  };
  const {
    window = defaultWindow,
    observeOnMount,
    ...observerOptions
  } = options;
  const isSupported = window && 'ResizeObserver' in window;
  let observer;
  function cleanup() {
    if (observer) {
      observer.disconnect();
      observer = undefined;
    }
  }
  function stop() {
    cleanup();
    stopWatch();
  }
  const stopWatch = watch(target, el => {
    cleanup();
    if (isSupported && window && el) {
      observer = new window.ResizeObserver(callback);
      if (observeOnMount) observer.observe(el, observerOptions);
    }
  }, {
    immediate: true,
    flush: 'post'
  });
  function observe() {
    unobserve();
    if (isSupported && target.value) observer.observe(target.value);
  }
  function unobserve() {
    if (isSupported && target.value) observer.unobserve(target.value);
  }
  onBeforeUnmount(() => {
    stop();
  });
  return {
    isSupported,
    stop,
    observe,
    unobserve
  };
}
function useResizeObserver2(callback) {
  const resizeRef = ref();
  const contentRect = ref();
  const contentBoxSize = ref();
  const borderBoxSize = ref();
  if (IN_BROWSER) {
    const observer = new ResizeObserver(entries => {
      callback === null || callback === void 0 ? void 0 : callback(entries, observer);
      if (!entries.length) return;
      contentRect.value = entries[0].contentRect;
      contentBoxSize.value = entries[0].contentBoxSize[0];
      borderBoxSize.value = entries[0].borderBoxSize[0];
    });
    onBeforeUnmount(() => {
      observer.disconnect();
    });
    watch(resizeRef, (newValue, oldValue) => {
      if (oldValue) {
        observer.unobserve(oldValue);
        contentRect.value = undefined;
        contentBoxSize.value = undefined;
        borderBoxSize.value = undefined;
      }
      if (newValue) observer.observe(newValue);
    }, {
      flush: 'post'
    });
  }
  return {
    resizeRef,
    contentRect: readonly(contentRect),
    contentBoxSize: readonly(contentBoxSize),
    borderBoxSize: readonly(borderBoxSize)
  };
}
export { useResizeObserver, useResizeObserver2 };